/**
 * "Take me there →" — the link from a lesson to the live screen.
 *
 * ⚠️ IT OPENS A SECOND TAB, AND THAT IS THE POINT (product owner, 2026-08-06, relaying a
 * tester): following the link in place meant reading a step, navigating away to
 * do it, then navigating back for the next one — "annoying to bounce back and
 * forth". The guide now stays put where you are reading it, and the tracker
 * follows along beside it.
 *
 * ⚠️ A NAMED TARGET, NOT `_blank`. Every one of these links shares the same
 * `ew-tracker` tab, so a walkthrough with a link on most slides leaves the
 * reader with two tabs rather than thirty, and one signed-in session rather
 * than thirty — sessionStorage survives navigation within a tab, so the MSAL
 * cache the tab inherited at birth is still there on the next jump.
 *
 * It does NOT keep the running app alive: each jump navigates that tab to a new
 * document, so the SPA boots again. Avoiding that would mean holding the tab
 * open and routing it over a cross-tab channel (BroadcastChannel/postMessage) —
 * a remote control between tabs, for a re-parse of a cached bundle. Not worth
 * the machinery for the problem being solved, which was the bouncing.
 *
 * ⚠️ No `rel="noopener"`, deliberately. The destination is this same app, so
 * noopener buys nothing — and it would make the new tab a fresh browsing
 * context rather than one opened FROM this one. This app keeps its MSAL token
 * cache in sessionStorage (`lib/msalConfig.ts`), which only the latter inherits;
 * a severed context would meet the reader with the sign-in card instead of the
 * screen they asked for.
 */

/** One tab for the whole guide, reused by every link. */
export const APP_TAB = 'ew-tracker';

/**
 * Stop THIS tab from being the tracker tab.
 *
 * ⚠️ A tab keeps its name across same-origin navigation, and the `?` Help
 * drawer links to /training from every page in the app. So a reader who jumps
 * to the tracker and then opens the guide from there is reading it in a tab
 * still called `ew-tracker` — and every "Take me there" in it resolves to the
 * tab it is already in, silently restoring exactly the same-tab bouncing this
 * was built to remove (review, #310).
 *
 * The guide's tab is therefore never the tracker's. Renaming rather than
 * refusing to link: the reader gets a fresh tracker tab and keeps the guide
 * where they are reading it, which is the promise either way.
 */
export function releaseAppTab(w: Pick<Window, 'name'> = window): void {
  // Only ever clears a name we set. Nothing else in the app names a tab, but
  // blanking someone else's would be an odd thing for the guide to do.
  if (w.name === APP_TAB) w.name = '';
}

export default function TakeMeThere({ href, linkClass }: {
  href: string;
  /** The host page's own link styling — the two faces of the guide size it differently. */
  linkClass: string;
}) {
  return (
    <div className="tmt">
      <a className={linkClass} href={href} target={APP_TAB}>Take me there →</a>
      {/* Said out loud rather than left as a surprise: a link that moves a tab
          you weren't expecting it to move is worse than one that warns you. */}
      <span className="tmt-note">opens in a new tab — this guide stays open here</span>
    </div>
  );
}

/** Shared by both faces of the guide, which each embed their own stylesheet. */
export const takeMeThereCss = `
  .tmt { display: flex; align-items: baseline; gap: 9px; flex-wrap: wrap; }
  .tmt-note { font: var(--ew-fw-regular) 11.5px/1.5 var(--ew-font); color: var(--ew-n-500); }
`;
